import api from '@/social/API';
import eventBus from '@/messaging/event-bus';
import { Account, ReconnectRequest } from '@/social/types';
import { getCredentials } from '@/iam/external';

export class ReconnectRequestEvent {
  reconnectRequest: ReconnectRequest;

  constructor(reconnectRequest: ReconnectRequest) {
    this.reconnectRequest = reconnectRequest;
  }
}

export class ReconnectRequestCreated {
  static eventName = 'reconnect_request.created';

  reconnectRequest: ReconnectRequest;

  constructor(reconnectRequest: ReconnectRequest) {
    this.reconnectRequest = reconnectRequest;
  }
}

export class ReconnectRequestCanceled extends ReconnectRequestEvent {
  static eventName = 'reconnect_request.canceled';
}

export class AccountReconnected {
  static eventName = 'account.reconnected';

  account: Account;

  constructor(account: Account) {
    this.account = account;
  }
}

export class RemindersEnabled extends ReconnectRequestEvent {
  static eventName = 'reconnect_request.reminders_enabled';
}

export class RemindersDisabled extends ReconnectRequestEvent {
  static eventName = 'reconnect_request.reminders_disabled';
}

export function createRequest(accountId: number): Promise<ReconnectRequest> {
  return api
    .post(`accounts/${accountId}/reconnect-requests`)
    .then((response) => {
      const reconnectRequest = (response.data as unknown as ReconnectRequest);
      eventBus.$emit(
        ReconnectRequestCreated.eventName,
        new ReconnectRequestCreated(reconnectRequest),
      );
      return reconnectRequest;
    });
}

export function canCancel(request: ReconnectRequest): boolean {
  return (request.creatorType !== 'system');
}

export function cancelRequest(request: ReconnectRequest): Promise<void> {
  return api
    .delete(`reconnect-requests/${request.id}`)
    .then(() => {
      eventBus.$emit(
        ReconnectRequestCanceled.eventName,
        new ReconnectRequestCanceled(request),
      );
    });
}

export async function reconnect(account: Account): Promise<void> {
  const { socialSite } = account;
  const { auth } = socialSite;
  const credentials = await getCredentials(socialSite.type, auth);
  if (credentials === null) return;
  const response = await api.put(`accounts/${account.id}/reconnect`, { auth, credentials });
  const reconnectedAccount = (response.data as Account);
  const event = new AccountReconnected(reconnectedAccount);
  eventBus.$emit(AccountReconnected.eventName, event);
}

export function disableReminders(requestId: number): Promise<ReconnectRequest> {
  return api
    .put(`reconnect-requests/${requestId}/disable-reminders`)
    .then((response) => {
      const reconnectRequest = response.data;
      eventBus.$emit(
        RemindersDisabled.eventName,
        new RemindersDisabled(reconnectRequest),
      );
      return reconnectRequest;
    });
}

export function enableReminders(requestId: number): Promise<ReconnectRequest> {
  return api
    .put(`reconnect-requests/${requestId}/enable-reminders`)
    .then((response) => {
      const reconnectRequest = response.data;
      eventBus.$emit(
        RemindersEnabled.eventName,
        new RemindersEnabled(reconnectRequest),
      );
      return reconnectRequest;
    });
}
